import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useRef,
    ReactNode,
} from 'react'

interface TourHandlerContextType {
    /** Register a custom tour handler for current page */
    registerHandler: (handler: () => void) => void
    /** Unregister the handler */
    unregisterHandler: () => void
    /** Check if custom handler exists */
    hasCustomHandler: boolean
    /** Execute the custom handler */
    executeHandler: () => void
}

const TourHandlerContext = createContext<TourHandlerContextType | null>(null)

/** Keyboard shortcut: Ctrl+Shift+H or ? to trigger tour */
const TOUR_SHORTCUTS = {
    ctrlShiftH: (e: KeyboardEvent) => e.ctrlKey && e.shiftKey && e.key === 'H',
    questionMark: (e: KeyboardEvent) =>
        e.key === '?' && !e.ctrlKey && !e.metaKey,
}

export const TourHandlerProvider = ({ children }: { children: ReactNode }) => {
    const [handler, setHandler] = useState<(() => void) | null>(null)
    const handlerRef = useRef<(() => void) | null>(null)

    // Keep ref in sync with state for keyboard handler
    useEffect(() => {
        handlerRef.current = handler
    }, [handler])

    const registerHandler = useCallback((newHandler: () => void) => {
        setHandler(() => newHandler)
    }, [])

    const unregisterHandler = useCallback(() => {
        setHandler(null)
    }, [])

    const executeHandler = useCallback(() => {
        if (handler) {
            handler()
        }
    }, [handler])

    // Global keyboard shortcut listener
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ignore if typing in input/textarea
            const target = e.target as HTMLElement
            if (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            ) {
                return
            }

            // Check shortcuts
            if (
                TOUR_SHORTCUTS.ctrlShiftH(e) ||
                TOUR_SHORTCUTS.questionMark(e)
            ) {
                e.preventDefault()
                if (handlerRef.current) {
                    handlerRef.current()
                }
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    return (
        <TourHandlerContext.Provider
            value={{
                registerHandler,
                unregisterHandler,
                hasCustomHandler: !!handler,
                executeHandler,
            }}
        >
            {children}
        </TourHandlerContext.Provider>
    )
}

export const useTourHandler = () => {
    const context = useContext(TourHandlerContext)
    if (!context) {
        throw new Error(
            'useTourHandler must be used within TourHandlerProvider'
        )
    }
    return context
}
